<script setup lang="ts">
    import IconAlignCenter from '@/components/icons/IconAlignCenter.vue';
    import IconAlignLeft from '@/components/icons/IconAlignLeft.vue';
    import IconAlignRight from '@/components/icons/IconAlignRight.vue';
    import IconJustify from '@/components/icons/IconJustify.vue';
    import BaseToggleButton from '@/components/BaseToggleButton.vue';
    import { useTemplateStore } from '@/store/template.store';
    import { updateAlignment } from '@/commons/fabricjs/FabricTextHelper';
    import { ETextAlignement } from '@/typescript/enums/ETextAlignement';
    import { dataStore } from '@/commons/DataStore';
    import { useConfigStore } from '@/store/config.store';
    import { storeToRefs } from 'pinia';
    import type { ITextboxField } from '@/typescript/interfaces/ITextboxField';
    import type { ITextboxCustom } from '@/typescript/interfaces/ITextboxCustom';

    const { field } = defineProps<{ field: ITextboxField }>();
    const { currentSide } = storeToRefs(useConfigStore());
    const templateStore = useTemplateStore();

    const icons = [
        { component: IconAlignLeft, value: ETextAlignement.LEFT, label: 'Left' },
        { component: IconAlignCenter, value: ETextAlignement.CENTER, label: 'Center' },
        { component: IconAlignRight, value: ETextAlignement.RIGHT, label: 'Right' },
        { component: IconJustify, value: ETextAlignement.JUSTIFY, label: 'Justify' },
    ];

    function selectButton(value: ETextAlignement) {
        const fabricWorld = dataStore.getFabricWorld(currentSide.value);
        const fText = fabricWorld.getFabricElementById(field.id);
        if (fText === undefined || !['text', 'textbox'].includes(fText.type)) {
            return;
        }
        templateStore.updateField({ ...field, textAlign: value } as ITextboxField);
        updateAlignment(fabricWorld.fabricCanvas, fText as ITextboxCustom, value);
    }
</script>

<template>
    <div class="flex">
        <BaseToggleButton
            v-for="(icon, index) in icons"
            :key="index"
            :bordered="false"
            :model-value="field.textAlign === icon.value"
            class="mr-2"
            @update:model-value="selectButton(icon.value)"
        >
            <div class="flex h-full w-full items-center justify-center">
                <component
                    :is="icon.component"
                    class="h-4 w-5"
                />
                <span class="sr-only text-xs">{{ icon.label }}</span>
            </div>
        </BaseToggleButton>
    </div>
</template>
